import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const [formData, setFormData] = useState({
    // Account credentials (sent to backend)
    username: '',
    password: '',
    // Profile fields
    fullName: '',
    gender: 'Female',
    phone: '',
    permanentAddress: '',
    currentAddress: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    gotra: '',
    religion: 'Hindu',
    caste: 'Brahmin',
    motherTongue: 'Nepali',
    maritalStatus: 'Never Married',
    education: '',
    occupation: '',
    annualIncome: '',
    numBrothers: '0',
    numSisters: '0',
    preferredAgeRange: '23 - 29',
    preferredCaste: 'Any',
    photoPrivacy: true,
    photos: [null, null, null, null, null],
    primaryPhotoIndex: 0
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoChange = (index, file) => {
    setFormData((prev) => {
      const newPhotos = [...prev.photos];
      newPhotos[index] = file;
      return { ...prev, photos: newPhotos };
    });
  };

  const handleSetPrimaryPhoto = (index) => {
    setFormData((prev) => ({ ...prev, primaryPhotoIndex: index }));
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiError('');

    try {
      // Build payload — split name into first/last for Django User
      const [firstName, ...rest] = (formData.fullName || '').trim().split(' ');
      const lastName = rest.join(' ');

      const fd = new FormData();
      fd.append('username', formData.username || formData.phone);
      fd.append('password', formData.password);
      fd.append('password2', formData.password);
      fd.append('first_name', firstName || formData.fullName);
      fd.append('last_name', lastName || '');
      fd.append('email', '');
      fd.append('phone', formData.phone || '');
      fd.append('gender', formData.gender || '');
      fd.append('permanent_address', formData.permanentAddress || '');
      fd.append('current_address', formData.currentAddress || '');
      if (formData.birthDate) fd.append('date_of_birth', formData.birthDate);
      if (formData.birthTime) fd.append('time_of_birth', formData.birthTime);
      if (formData.birthPlace) fd.append('place_of_birth', formData.birthPlace);
      if (formData.gotra) fd.append('gotra', formData.gotra);
      if (formData.religion) fd.append('religion', formData.religion);
      if (formData.caste) fd.append('caste', formData.caste);
      if (formData.motherTongue) fd.append('mother_tongue', formData.motherTongue);
      if (formData.maritalStatus) fd.append('marital_status', formData.maritalStatus);
      if (formData.education) fd.append('education', formData.education);
      if (formData.occupation) fd.append('occupation', formData.occupation);
      if (formData.annualIncome) fd.append('annual_income', formData.annualIncome);
      if (formData.numBrothers) fd.append('number_of_brothers', formData.numBrothers);
      if (formData.numSisters) fd.append('number_of_sisters', formData.numSisters);
      if (formData.preferredCaste) fd.append('preferred_community', formData.preferredCaste);
      if (formData.preferredAgeRange) {
        const parts = formData.preferredAgeRange.split('-').map((s) => s.trim());
        if (parts[0]) fd.append('preferred_age_min', parts[0]);
        if (parts[1]) fd.append('preferred_age_max', parts[1]);
      }
      if (formData.prefLocation) fd.append('preferred_location', formData.prefLocation);
      if (formData.horoscope) fd.append('horoscope_preference', formData.horoscope);
      if (formData.prefNotes) fd.append('additional_preferences', formData.prefNotes);
      fd.append('photo_privacy', formData.photoPrivacy ? 'true' : 'false');

      // Append primary profile photo as 'photo'
      const primaryPhoto = formData.photos[formData.primaryPhotoIndex];
      if (primaryPhoto) {
        fd.append('photo', primaryPhoto);
      }

      // Append remaining non-primary photos as gallery_photo_1..4
      let galleryCount = 1;
      formData.photos.forEach((photoFile, idx) => {
        if (photoFile && idx !== formData.primaryPhotoIndex && galleryCount <= 4) {
          fd.append(`gallery_photo_${galleryCount}`, photoFile);
          galleryCount++;
        }
      });

      await register(fd);

      // Auto-logged in — mark submitted and redirect to matches
      setSubmitted(true);
      setTimeout(() => navigate('/consultation/matches'), 2200);

    } catch (err) {
      let msg = 'Registration failed. Please check your details and try again.';
      if (typeof err === 'object' && err !== null) {
        if (err.username) msg = `Username error: ${Array.isArray(err.username) ? err.username.join(', ') : err.username}`;
        else if (err.password) msg = `Password error: ${Array.isArray(err.password) ? err.password.join(', ') : err.password}`;
        else if (err.error) msg = err.error;
        else if (err.detail) msg = err.detail;
      }
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = (currentStep / 4) * 100;

  const photoLabels = [
    'Photo 1: Profile Picture (Headshot)',
    'Photo 2: Full-Length Photo',
    'Photo 3: Traditional Attire Photo',
    'Photo 4: Family Photo',
    'Photo 5: Additional Casual Photo'
  ];

  return (<>
      <div className="sec-wrapper" style={{ maxWidth: '840px' }}>
      {/* Unified Vivah Nepal Hero Banner */}
      <section className="hero-section">
        <div className="hero-content">
          <div style={{ marginBottom: '1.2rem' }}>
            <span className="badge-hero-top">
              ✦ FREE MARRIAGE PROFILE REGISTRATION ✦
            </span>
          </div>

          <h1 className="hero-title">
            Marriage Profile <span className="hero-title-gradient">Registration (100% Free)</span>
          </h1>

          <p className="hero-subtitle">
            Managed by renowned marriage consultant <strong>Laxmikant Timilsina</strong> (Tansen-09, Palpa | Office: Yogikuti, Butwal | 9857074103). Personalized Kundali evaluation & curated matchmaking.
          </p>

          <div className="hero-cta-group">
            <button className="btn btn-secondary" onClick={() => navigate('/consultation/login')}>
              Sign In to Existing Profile →
            </button>
            <a href="tel:9857074103" className="btn btn-tertiary">
              📞 Call Laxmikant (9857074103)
            </a>
          </div>
        </div>
      </section>

        {/* Wizard Stepper Progress Header */}
        {!submitted && (
          <div className="card" style={{ marginBottom: '2rem', padding: '1.2rem 1.4rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem', fontSize: 'clamp(0.72rem, 2vw, 0.86rem)', fontWeight: 800 }}>
              <span style={{ color: currentStep >= 1 ? '#9E1B32' : '#A0908C' }}>1. Contact</span>
              <span style={{ color: currentStep >= 2 ? '#9E1B32' : '#A0908C' }}>2. Kundali</span>
              <span style={{ color: currentStep >= 3 ? '#9E1B32' : '#A0908C' }}>3. Profession</span>
              <span style={{ color: currentStep >= 4 ? '#9E1B32' : '#A0908C' }}>4. Photos & Prefs</span>
            </div>

            {/* Animated Progress Bar */}
            <div style={{ height: '8px', background: '#E8DFD1', borderRadius: '9999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPercentage}%`, background: 'linear-gradient(135deg, #9E1B32 0%, #D4AF37 100%)', transition: 'width 0.4s ease' }} />
            </div>
          </div>
        )}

        {submitted ? (
          <div className="card" style={{ textAlign: 'center', border: '1.5px solid #D4AF37', padding: '2.5rem 1.5rem' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '0.8rem' }}>🎉</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#e8f5e9', color: '#2e7d32', padding: '0.45rem 1.2rem',
              borderRadius: '9999px', fontSize: '0.86rem', fontWeight: 800, marginBottom: '1rem'
            }}>✓ 100% Free Account Created & Logged In</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '2.2rem', color: '#9E1B32', marginBottom: '0.5rem' }}>
              Welcome, {formData.fullName}! 🙏
            </h2>
            <p style={{ color: '#6E615E', maxWidth: '620px', margin: '0 auto 1.2rem', fontSize: '0.94rem', lineHeight: 1.7 }}>
              Your profile has been created for <strong>100% Free (NPR 0)</strong>! To start searching and connecting with verified matches, choose between <strong>Consultation Membership</strong> (NPR 21,000 for 6 months match search) or <strong>Full Membership</strong> (NPR 50,000 for unlimited match search).
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={() => navigate('/consultation/matches')}>
                🔍 View Curated Matches →
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/consultation/payment')}>
                💎 Upgrade Membership (QR Payment) →
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* API Error */}
            {apiError && (
              <div style={{
                background: '#FEECEC', color: '#9E1B32', border: '1px solid rgba(158,27,50,0.3)',
                borderRadius: '14px', padding: '0.9rem 1.2rem', marginBottom: '1.5rem',
                fontSize: '0.88rem', fontWeight: 600
              }}>
                ⚠️ {apiError}
              </div>
            )}
            {/* Step 1: Personal &amp; Contact Info */}
            {currentStep === 1 && (
              <div className="card">
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9E1B32', marginBottom: '1.2rem', fontFamily: 'var(--serif)', borderBottom: '1px solid #E8DFD1', paddingBottom: '0.6rem' }}>
                  Step 1: Personal & Contact Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Full Name *</label>
                    <input type="text" name="fullName" className="form-input" placeholder="e.g. Ramesh Sharma" value={formData.fullName} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Gender *</label>
                    <select name="gender" className="form-input" value={formData.gender} onChange={handleChange}>
                      <option value="Female">Bride (Female)</option>
                      <option value="Male">Groom (Male)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Phone Number *</label>
                    <input type="tel" name="phone" className="form-input" placeholder="e.g. 98XXXXXXXX" value={formData.phone} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Permanent Address *</label>
                    <input type="text" name="permanentAddress" className="form-input" placeholder="e.g. Palpa / Kathmandu" value={formData.permanentAddress} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Current Address *</label>
                    <input type="text" name="currentAddress" className="form-input" placeholder="e.g. Yogikuti, Butwal" value={formData.currentAddress} onChange={handleChange} required />
                  </div>
                </div>

                {/* Account Credentials */}
                <div style={{ marginTop: '1.5rem', padding: '1.2rem', background: '#FBF0F2', borderRadius: '16px', border: '1px solid rgba(158,27,50,0.15)' }}>
                  <h4 style={{ fontSize: '0.92rem', fontWeight: 800, color: '#9E1B32', marginBottom: '1rem' }}>🔐 Create Your Login Credentials</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Username *</label>
                      <input type="text" name="username" className="form-input" placeholder="e.g. ramesh_sharma" value={formData.username} onChange={handleChange} required autoComplete="username" />
                      <p style={{ fontSize: '0.72rem', color: '#6E615E', marginTop: '0.3rem' }}>Used to log in to your profile</p>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Password *</label>
                      <input type="password" name="password" className="form-input" placeholder="Min. 6 characters" value={formData.password} onChange={handleChange} required minLength={6} autoComplete="new-password" />
                      <p style={{ fontSize: '0.72rem', color: '#6E615E', marginTop: '0.3rem' }}>Keep this safe — you'll need it to log in</p>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-primary" onClick={nextStep}>
                    Next: Kundali Details →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Kundali & Birth Details */}
            {currentStep === 2 && (
              <div className="card" style={{ border: '1.5px solid #D4AF37' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', borderBottom: '1px solid #E8DFD1', paddingBottom: '0.6rem' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9E1B32', fontFamily: 'var(--serif)' }}>
                    Step 2: Horoscope & Kundali Matching
                  </h3>
                  <span className="badge-light" style={{ background: '#FAF6ED', color: '#8C6A23' }}>
                    Authentic Kundali Matching
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Date of Birth *</label>
                    <input type="date" name="birthDate" className="form-input" value={formData.birthDate} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Exact Birth Time *</label>
                    <input type="text" name="birthTime" className="form-input" placeholder="e.g. 06:45 AM" value={formData.birthTime} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Birth Place (City/District) *</label>
                    <input type="text" name="birthPlace" className="form-input" placeholder="e.g. Butwal / Tansen" value={formData.birthPlace} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Gotra *</label>
                    <input type="text" name="gotra" className="form-input" placeholder="e.g. Kasyap / Bharadwaj" value={formData.gotra} onChange={handleChange} required />
                  </div>
                </div>

                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'space-between' }}>
                  <button type="button" className="btn btn-tertiary" style={{ color: '#1C1014', borderColor: '#E8DFD1' }} onClick={prevStep}>
                    ← Back
                  </button>
                  <button type="button" className="btn btn-primary" onClick={nextStep}>
                    Next: Profession & Family →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Socio-Cultural, Profession & Family */}
            {currentStep === 3 && (
              <div className="card">
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9E1B32', marginBottom: '1.2rem', fontFamily: 'var(--serif)', borderBottom: '1px solid #E8DFD1', paddingBottom: '0.6rem' }}>
                  Step 3: Profession, Education & Family Details
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Caste / Ethnicity *</label>
                    <input type="text" name="caste" className="form-input" placeholder="e.g. Brahmin, Chhetri, Newar" value={formData.caste} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Education Qualification *</label>
                    <input type="text" name="education" className="form-input" placeholder="e.g. Master's in Engineering" value={formData.education} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Occupation / Job *</label>
                    <input type="text" name="occupation" className="form-input" placeholder="e.g. Software Engineer / Banking" value={formData.occupation} onChange={handleChange} required />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Annual Income (NPR)</label>
                    <input type="text" name="annualIncome" className="form-input" placeholder="e.g. 8 Lakhs +" value={formData.annualIncome} onChange={handleChange} />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Number of Brothers</label>
                    <input type="number" name="numBrothers" className="form-input" min="0" value={formData.numBrothers} onChange={handleChange} />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Number of Sisters</label>
                    <input type="number" name="numSisters" className="form-input" min="0" value={formData.numSisters} onChange={handleChange} />
                  </div>
                </div>

                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'space-between' }}>
                  <button type="button" className="btn btn-tertiary" style={{ color: '#1C1014', borderColor: '#E8DFD1' }} onClick={prevStep}>
                    ← Back
                  </button>
                  <button type="button" className="btn btn-primary" onClick={nextStep}>
                    Next: 5 Photos & Preferences →
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: 5 Photo Uploads & Primary Profile Picture Selector */}
            {currentStep === 4 && (
              <div className="card">
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9E1B32', marginBottom: '1.2rem', fontFamily: 'var(--serif)', borderBottom: '1px solid #E8DFD1', paddingBottom: '0.6rem' }}>
                  Step 4: Upload Your Photos (Up to 5 Photos)
                </h3>

                <p style={{ fontSize: '0.86rem', color: '#6E615E', marginBottom: '1.5rem' }}>
                  Upload up to <strong>5 marriage photographs</strong>. Mark one photo as your <strong>⭐ Primary Profile Picture</strong> to display as your main photo.
                </p>

                {/* 5 Photo Slot Grid */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginBottom: '1.8rem' }}>
                  {photoLabels.map((label, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '1.2rem',
                        borderRadius: '16px',
                        border: formData.primaryPhotoIndex === idx ? '2px solid #D4AF37' : '1px solid #E8DFD1',
                        background: formData.primaryPhotoIndex === idx ? '#FAF6ED' : '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: '220px' }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#9E1B32', marginBottom: '0.2rem' }}>
                          {label} {formData.primaryPhotoIndex === idx && <span className="badge-light" style={{ marginLeft: '0.4rem', fontSize: '0.72rem' }}>⭐ Primary Profile Photo</span>}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoChange(idx, e.target.files[0])}
                          style={{ fontSize: '0.82rem' }}
                        />
                      </div>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', fontWeight: 700, color: '#1C1014', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="primaryPhoto"
                          checked={formData.primaryPhotoIndex === idx}
                          onChange={() => handleSetPrimaryPhoto(idx)}
                          style={{ accentColor: '#9E1B32', width: '18px', height: '18px' }}
                        />
                        Set as Main Profile Picture
                      </label>
                    </div>
                  ))}
                </div>

                {/* Partner Preferences & Privacy */}
                <div style={{ borderTop: '1px solid #E8DFD1', paddingTop: '1.2rem', marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#9E1B32', marginBottom: '1rem', fontFamily: 'var(--serif)' }}>
                    Partner Preferences & Privacy Shield
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem', marginBottom: '1.2rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Preferred Age Range</label>
                      <input type="text" name="preferredAgeRange" className="form-input" placeholder="e.g. 24 - 30 Yrs" value={formData.preferredAgeRange} onChange={handleChange} />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>Preferred Caste / Community</label>
                      <input type="text" name="preferredCaste" className="form-input" placeholder="e.g. Brahmin / Any" value={formData.preferredCaste} onChange={handleChange} />
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.86rem', color: '#1C1014', cursor: 'pointer' }}>
                    <input type="checkbox" name="photoPrivacy" checked={formData.photoPrivacy} onChange={handleChange} style={{ accentColor: '#9E1B32', width: '18px', height: '18px' }} />
                    🔒 Enable Photo Privacy Shield (Blur photos until mutually accepted)
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem' }}>
                  <button type="button" className="btn btn-tertiary" style={{ color: '#1C1014', borderColor: '#E8DFD1' }} onClick={prevStep}>
                    ← Back
                  </button>

                  <button
                    type="submit"
                    className="btn btn-secondary"
                    style={{ padding: '0.85rem 1.6rem', fontSize: '0.92rem', opacity: loading ? 0.7 : 1 }}
                    disabled={loading}
                  >
                    {loading ? '⏳ Creating Account & Logging In…' : 'Complete Registration →'}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
  
  </>);
}